import { CheckCircle, DoDisturbAlt } from '@mui/icons-material';
import { Paper, Typography } from '@mui/material';
import { format } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

import { DurchgangRessourcenCalendar, FilterParameters } from '../../../components/general';
import { PlanningWarning } from '../../../components/general/PlanningWarningDialog.tsx';
import { CalendarEvent } from '../../../components/general/ResourceCalendar';
import { Timeline } from '../../../components/general/Timeline';
import {
  CheckDurchgangAvailabilityOutput,
  ExperteFromResourcesRouter,
  ThemaFromThemaRouter,
  Zeitraum,
} from '../../../dtos';
import { SaveButtonState } from '../DurchgangPlanung.types';
import { content } from './CreateDurchgangResult.content';
import { CalendarData } from './CreateDurchgangResult.types.ts';
import {
  getAppliedModulterminChanges,
  getMissingGruppenraumWarnings,
  getSaveInputData,
} from './CreateDurchgangResult.utils.ts';
import { CreateDurchgangResultModul } from './CreateDurchgangResultModul';

type CreateDurchgangResultProps = {
  readonly thema: Pick<
    ThemaFromThemaRouter,
    'akaThemaId' | 'planungsinformation' | 'isOnline' | 'planungsformat' | 'module'
  >;
  readonly availabilityResult: CheckDurchgangAvailabilityOutput;
  readonly expertePool:
    Pick<ExperteFromResourcesRouter, 'experteSapId' | 'vorname' | 'nachname' | 'istGesperrt'>[] | undefined;
  readonly setSaveButtonState: Dispatch<SetStateAction<SaveButtonState>>;
  readonly onPlanningWarningsChange: Dispatch<SetStateAction<PlanningWarning[]>>;
  readonly filterParams: FilterParameters;
};

export const CreateDurchgangResult = ({
  thema,
  availabilityResult,
  expertePool,
  setSaveButtonState,
  onPlanningWarningsChange,
  filterParams,
}: CreateDurchgangResultProps) => {
  const [calendarData, setCalendarData] = useState<CalendarData | undefined>(undefined);
  const [planResultData, setPlanResultData] = useState(availabilityResult);

  const getModulResult = useCallback(
    (modulnummer: number): CheckDurchgangAvailabilityOutput['module'][number] => {
      const modulResult = planResultData.module.find((modul) => modul.modulnummer === modulnummer);
      if (!modulResult) {
        throw new Error(`Verfügbarkeits-Ergebnis für Modul ${modulnummer} konnte nicht gefunden werden.`);
      }
      return modulResult;
    },
    [planResultData.module],
  );

  const hasTimeOverlapWithOtherModule = useCallback(
    (modulResult: CheckDurchgangAvailabilityOutput['module'][number]): boolean => {
      for (const otherModul of planResultData.module) {
        if (otherModul.modulnummer === modulResult.modulnummer) {
          continue;
        }

        for (const otherTag of otherModul.tage) {
          for (const tag of modulResult.tage) {
            const startsDuringOther =
              tag.zeitraum.start >= otherTag.zeitraum.start && tag.zeitraum.start < otherTag.zeitraum.end;
            const endsDuringOther =
              tag.zeitraum.end > otherTag.zeitraum.start && tag.zeitraum.end <= otherTag.zeitraum.end;
            const surroundsOther =
              tag.zeitraum.start <= otherTag.zeitraum.start && tag.zeitraum.end >= otherTag.zeitraum.end;

            if (startsDuringOther || endsDuringOther || surroundsOther) {
              return true;
            }
          }
        }
      }

      return false;
    },
    [planResultData.module],
  );

  const hasUnavailableTrainer = useCallback(
    (modulResult: CheckDurchgangAvailabilityOutput['module'][number]): boolean => !modulResult.available,
    [],
  );

  const hasUnavailableSeminarraum = useCallback(
    (modulResult: CheckDurchgangAvailabilityOutput['module'][number]): boolean =>
      Boolean(modulResult.seminarraum) && !modulResult.seminarraum?.available,
    [],
  );

  const hasUnavailableGruppenraum = useCallback(
    (modulResult: CheckDurchgangAvailabilityOutput['module'][number]): boolean =>
      modulResult.tage.some((tag) => tag.gruppenraeume?.some((gruppenraum) => !gruppenraum.available) === true),
    [],
  );

  const hasErrors = useCallback(
    (modulResult: CheckDurchgangAvailabilityOutput['module'][number]): boolean =>
      hasTimeOverlapWithOtherModule(modulResult) ||
      hasUnavailableTrainer(modulResult) ||
      hasUnavailableSeminarraum(modulResult) ||
      hasUnavailableGruppenraum(modulResult),
    [hasTimeOverlapWithOtherModule, hasUnavailableSeminarraum, hasUnavailableTrainer, hasUnavailableGruppenraum],
  );

  const onRescheduleManually = (modulResult: CheckDurchgangAvailabilityOutput['module'][number]) => {
    setCalendarData({
      open: true,
      initDate: modulResult.tage[0].zeitraum.start,
      suggestedEvents: {
        tage: modulResult.tage.map((t) => ({
          zeitraum: t.zeitraum,
          expertenSapIds: t.expertenSapIds,
          gruppenraumSapIds: t.gruppenraeume ? t.gruppenraeume.map((g) => g.raumSapId) : undefined,
        })),
        seminarraumSapId: modulResult.seminarraum ? modulResult.seminarraum.raumSapId : undefined,
      },
      zeitlicherAblauf: modulResult.tage.map((tag, index) => ({
        tagNr: index,
        startZeit: format(tag.zeitraum.start, 'HH:mm'),
        endZeit: format(tag.zeitraum.end, 'HH:mm'),
      })),
      modulnummer: modulResult.modulnummer,
      gruppenraumInfo: modulResult.gruppenraumInfo,
      isOnlineModul: Boolean(thema.module.find((m) => m.modulnummer === modulResult.modulnummer)?.isOnline),
      modulTage: modulResult.tage.length,
    });
  };

  useEffect(() => {
    const anyErrors = thema.module.some((modul) => hasErrors(getModulResult(modul.modulnummer)));
    setSaveButtonState({
      tooltip: anyErrors ? content.errorsAvailable : '',
      saveInputData: getSaveInputData(planResultData, thema.module),
      disabled: anyErrors,
    });

    onPlanningWarningsChange(getMissingGruppenraumWarnings(planResultData.module));
  }, [planResultData, getModulResult, hasErrors, setSaveButtonState, onPlanningWarningsChange, thema.module]);

  const onClose = () => {
    setCalendarData(undefined);
  };

  const onApply = (calendarEvents: readonly CalendarEvent[], ablauf: Zeitraum[]) => {
    const changes = getAppliedModulterminChanges(calendarEvents, ablauf, calendarData?.modulnummer, planResultData);

    if (changes) {
      setPlanResultData(changes);
    }

    onClose();
  };

  return (
    <>
      <Paper sx={{ p: 3, background: (theme) => theme.palette.surface.brandLight }}>
        <Typography variant="headingXxs">Planung</Typography>
        <Timeline
          items={thema.module.map((modul) => {
            const modulResult = getModulResult(modul.modulnummer);

            return {
              dot: hasErrors(modulResult) ? <DoDisturbAlt color="error" /> : <CheckCircle color="success" />,
              content: (
                <CreateDurchgangResultModul
                  modul={modul}
                  modulResult={modulResult}
                  expertePool={expertePool}
                  hasErrors={hasErrors}
                  onRescheduleManually={onRescheduleManually}
                />
              ),
            };
          })}
        />
      </Paper>
      {calendarData && (
        <DurchgangRessourcenCalendar
          initDate={calendarData.initDate}
          suggestedEvents={calendarData.suggestedEvents}
          zeitlicherAblauf={calendarData.zeitlicherAblauf}
          thema={thema}
          givenFilterParams={filterParams}
          isOnlineModul={calendarData.isOnlineModul}
          gruppenraumInfo={calendarData.gruppenraumInfo}
          modulTage={calendarData.modulTage}
          open={calendarData.open}
          onAbort={onClose}
          onApply={onApply}
        />
      )}
    </>
  );
};
